import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Session } from '@ory/client';
import { of, timer } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { Module } from '@aw/prypco/enums';
import { AuthFacade } from '@aw/prypco/state/auth';

import {
  OryFlowType,
  OryMessage,
  OryMessageCode,
} from '../constants/ory.constant';
import { OryAuthPages } from '../constants/pages.constant';
import { OryAuthService } from '../services/ory-auth.service';
import { getNodeValue } from '../utils/ory.helpers';
import * as OryAuthActions from './ory-auth.actions';
import { AuthError, LoginFlow, RegistrationFlow } from './ory-auth.models';
import {
  selectLoginFlow,
  selectRedirectTo,
  selectRegistrationFlow,
} from './ory-auth.selectors';

@Injectable()
export class OryAuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly oryAuthService = inject(OryAuthService);
  private readonly authFacade = inject(AuthFacade);

  // Session Effects
  loadSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OryAuthActions.loadSession),
      switchMap(() =>
        this.oryAuthService.getSession().pipe(
          map((session) => {
            if (session) {
              return OryAuthActions.loadSessionSuccess({ session });
            }

            return OryAuthActions.loadSessionFailure({
              error: { message: 'No active session' },
            });
          }),
          catchError((error) =>
            of(
              OryAuthActions.loadSessionFailure({
                error: this.mapError(error),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // Login Flow Effects
  startLoginFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OryAuthActions.startLoginFlow),
      switchMap(() =>
        this.oryAuthService.startBrowserLogin().pipe(
          switchMap((flowId) =>
            this.oryAuthService.getLoginFlow(flowId).pipe(
              map((flow) => {
                const csrfToken =
                  getNodeValue(flow.ui?.nodes, 'csrf_token') || '';

                return OryAuthActions.startLoginFlowSuccess({
                  flowId,
                  csrfToken,
                });
              }),
            ),
          ),
          catchError((error) => {
            // Check if user is already logged in
            if (
              error?.response?.data?.error?.id === 'session_already_available'
            ) {
              // Navigate to auth redirect page since user is already logged in
              return of(OryAuthActions.navigateToAuthRedirect({}));
            }

            return of(
              OryAuthActions.startLoginFlowFailure({
                error: this.mapError(error),
              }),
            );
          }),
        ),
      ),
    ),
  );

  loadLoginFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OryAuthActions.loadLoginFlow),
      switchMap(({ flowId }) =>
        this.oryAuthService.getLoginFlow(flowId).pipe(
          map((flow) => {
            const csrfToken = getNodeValue(flow.ui?.nodes, 'csrf_token') || '';
            return OryAuthActions.loadLoginFlowSuccess({
              flow: flow as LoginFlow,
              csrfToken,
            });
          }),
          catchError((error) => {
            if (error?.response?.status === 410) {
              return of(
                OryAuthActions.handleFlowExpired({
                  flowType: OryFlowType.LOGIN,
                }),
              );
            }
            return of(
              OryAuthActions.loadLoginFlowFailure({
                error: this.mapError(error),
              }),
            );
          }),
        ),
      ),
    ),
  );

  submitLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OryAuthActions.submitLogin),
      withLatestFrom(this.store.select(selectLoginFlow)),
      switchMap(([{ flowId, formData }, currentFlow]) => {
        const csrfToken =
          formData.csrf_token ||
          getNodeValue(currentFlow?.ui?.nodes, 'csrf_token');

        const body = {
          method: 'code',
          identifier: formData.email,
          csrf_token: csrfToken,
        };

        return this.oryAuthService.updateLoginFlow(flowId, body).pipe(
          switchMap(() => this.oryAuthService.getSession()),
          switchMap((session) => {
            if (session) {
              // User is fully authenticated
              return of(OryAuthActions.submitLoginSuccess({ session }));
            }
            // No session means we need to check the flow for next steps
            return this.oryAuthService.getLoginFlow(flowId).pipe(
              map((flow) => {
                // Check if the flow contains OTP verification nodes by looking for 'code' input
                const hasOtpNode =
                  !!getNodeValue(flow.ui?.nodes, 'code') ||
                  !!flow.ui?.nodes?.some(
                    (node) => node.attributes?.name === 'code',
                  );
                const requiresOtp = hasOtpNode || !session;

                return OryAuthActions.submitLoginSuccess({
                  flow: flow as LoginFlow,
                  requiresOtp,
                });
              }),
            );
          }),
          catchError((error) => {
            // Handle 400 status codes specially - check if response body is valid
            if (error?.response?.status === 400) {
              // Check if the error messages contain OtpCodeSent
              const messages = error?.response?.data?.ui?.messages || [];
              const hasOtpCodeSent = messages.some(
                (msg: OryMessage) => msg.id === OryMessageCode.OtpCodeSent,
              );

              // don't let the user know that the account doesn't exist
              const hasUserDoesntExistError = messages.some(
                (msg: OryMessage) =>
                  msg.id === OryMessageCode.AccountDoesntExist,
              );

              // If there are error messages and none of them is OtpCodeSent, treat as failure
              const hasErrorMessages = messages.some(
                (msg: OryMessage) => msg.type === 'error',
              );

              if (
                hasErrorMessages &&
                !hasOtpCodeSent &&
                !hasUserDoesntExistError
              ) {
                return of(
                  OryAuthActions.submitLoginFailure({
                    error: this.mapError(error),
                  }),
                );
              }

              // Try to handle as if it was successful, ignoring the 400 status
              try {
                return this.oryAuthService.getSession().pipe(
                  switchMap((session) => {
                    if (session) {
                      // User is fully authenticated
                      return of(OryAuthActions.submitLoginSuccess({ session }));
                    }
                    // No session means we need to check the flow for next steps
                    return this.oryAuthService.getLoginFlow(flowId).pipe(
                      map((flow) => {
                        // Check if the flow contains OTP verification nodes by looking for 'code' input
                        const hasOtpNode =
                          !!getNodeValue(flow.ui?.nodes, 'code') ||
                          !!flow.ui?.nodes?.some(
                            (node) => node.attributes?.name === 'code',
                          );
                        const requiresOtp = hasOtpNode || !session;

                        return OryAuthActions.submitLoginSuccess({
                          flow: flow as LoginFlow,
                          requiresOtp,
                        });
                      }),
                    );
                  }),
                  catchError(() =>
                    of(
                      OryAuthActions.submitLoginFailure({
                        error: this.mapError(error),
                      }),
                    ),
                  ),
                );
              } catch {
                // If anything fails, treat as actual error
                return of(
                  OryAuthActions.submitLoginFailure({
                    error: this.mapError(error),
                  }),
                );
              }
            }

            // Check for self_service_flow_expired error and reload the page
            if (
              error?.response?.status === 410 ||
              error?.response?.data?.error?.id === 'self_service_flow_expired'
            ) {
              // Reload the page to get a fresh flow
              window.location.reload();
              return of(
                OryAuthActions.handleFlowExpired({
                  flowType: OryFlowType.LOGIN,
                }),
              );
            }

            return of(
              OryAuthActions.submitLoginFailure({
                error: this.mapError(error),
              }),
            );
          }),
        );
      }),
    ),
  );

  // Registration Flow Effects
  startRegistrationFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OryAuthActions.startRegistrationFlow),
      switchMap(() =>
        this.oryAuthService.startBrowserRegistration().pipe(
          concatMap((flowId) =>
            this.oryAuthService.getRegistrationFlow(flowId).pipe(
              map((flow) => {
                const csrfToken =
                  getNodeValue(flow.ui?.nodes, 'csrf_token') || '';

                return OryAuthActions.startRegistrationFlowSuccess({
                  flowId,
                  csrfToken,
                });
              }),
            ),
          ),
          catchError((error) =>
            of(
              OryAuthActions.startRegistrationFlowFailure({
                error: this.mapError(error),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadRegistrationFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OryAuthActions.loadRegistrationFlow),
      switchMap(({ flowId }) =>
        this.oryAuthService.getRegistrationFlow(flowId).pipe(
          map((flow) => {
            const csrfToken = getNodeValue(flow.ui?.nodes, 'csrf_token') || '';
            return OryAuthActions.loadRegistrationFlowSuccess({
              flow: flow as RegistrationFlow,
              csrfToken,
            });
          }),
          catchError((error) => {
            if (error?.response?.status === 410) {
              return of(
                OryAuthActions.handleFlowExpired({
                  flowType: OryFlowType.REGISTRATION,
                }),
              );
            }
            return of(
              OryAuthActions.loadRegistrationFlowFailure({
                error: this.mapError(error),
              }),
            );
          }),
        ),
      ),
    ),
  );

  submitRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OryAuthActions.submitRegistration),
      withLatestFrom(this.store.select(selectRegistrationFlow)),
      switchMap(([{ flowId, formData }, currentFlow]) => {
        const csrfToken =
          formData.csrf_token ||
          getNodeValue(currentFlow?.ui?.nodes, 'csrf_token');

        const body = {
          method: 'code',
          'traits.email': formData.email,
          'traits.name.first': formData.firstName,
          'traits.name.last': formData.lastName,
          'traits.phone': formData.phoneNumber,
          csrf_token: csrfToken,
        };

        return this.oryAuthService.updateRegistrationFlow(flowId, body).pipe(
          switchMap(() => this.oryAuthService.getSession()),
          switchMap((session) => {
            if (session) {
              // User is fully authenticated
              return of(OryAuthActions.submitRegistrationSuccess({ session }));
            }
            // No session means we need to check the flow for next steps
            return this.oryAuthService.getRegistrationFlow(flowId).pipe(
              map((flow) => {
                // Check if the flow contains OTP verification nodes by looking for 'code' input
                const hasOtpNode =
                  !!getNodeValue(flow.ui?.nodes, 'code') ||
                  !!flow.ui?.nodes?.some(
                    (node) => node.attributes?.name === 'code',
                  );
                const requiresOtp = hasOtpNode || !session;

                return OryAuthActions.submitRegistrationSuccess({
                  flow: flow as RegistrationFlow,
                  requiresOtp,
                });
              }),
            );
          }),
          catchError((error) => {
            // Handle 400 status codes specially - check if response body is valid
            if (error?.response?.status === 400) {
              // Try to handle as if it was successful, ignoring the 400 status
              try {
                return this.oryAuthService.getSession().pipe(
                  switchMap((session) => {
                    if (session) {
                      // User is fully authenticated
                      return of(
                        OryAuthActions.submitRegistrationSuccess({ session }),
                      );
                    }
                    // No session means we need to check the flow for next steps
                    return this.oryAuthService.getRegistrationFlow(flowId).pipe(
                      map((flow) => {
                        // Check if the flow contains OTP verification nodes by looking for 'code' input
                        const hasOtpNode =
                          !!getNodeValue(flow.ui?.nodes, 'code') ||
                          !!flow.ui?.nodes?.some(
                            (node) => node.attributes?.name === 'code',
                          );
                        const requiresOtp = hasOtpNode || !session;

                        return OryAuthActions.submitRegistrationSuccess({
                          flow: flow as RegistrationFlow,
                          requiresOtp,
                        });
                      }),
                    );
                  }),
                  catchError(() =>
                    of(
                      OryAuthActions.submitRegistrationFailure({
                        error: this.mapError(error),
                      }),
                    ),
                  ),
                );
              } catch {
                // If anything fails, treat as actual error
                return of(
                  OryAuthActions.submitRegistrationFailure({
                    error: this.mapError(error),
                  }),
                );
              }
            }

            // Check for self_service_flow_expired error and reload the page
            if (
              error?.response?.status === 410 ||
              error?.response?.data?.error?.id === 'self_service_flow_expired'
            ) {
              // Reload the page to get a fresh flow
              window.location.reload();
              return of(
                OryAuthActions.handleFlowExpired({
                  flowType: OryFlowType.REGISTRATION,
                }),
              );
            }

            return of(
              OryAuthActions.submitRegistrationFailure({
                error: this.mapError(error),
              }),
            );
          }),
        );
      }),
    ),
  );

  // OTP Effects
  submitOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OryAuthActions.submitOtp),
      switchMap(
        ({
          flowId,
          flowType,
          csrfToken,
          otpFormData,
          loginData,
          registrationData,
        }) => {
          let payload: any = {
            method: 'code',
            code: otpFormData.code,
            csrf_token: csrfToken,
          };

          // For registration flow, include the original registration data
          if (flowType === OryFlowType.REGISTRATION && registrationData) {
            payload = {
              ...payload,
              'traits.email': registrationData.email,
              'traits.name.first': registrationData.firstName,
              'traits.name.last': registrationData.lastName,
              'traits.phone': registrationData.phoneNumber,
            };
          } else if (flowType === OryFlowType.LOGIN && loginData) {
            payload = {
              ...payload,
              identifier: loginData.email,
            };
          }

          const updateFlow$ =
            flowType === OryFlowType.LOGIN
              ? this.oryAuthService.updateLoginFlow(flowId, payload)
              : this.oryAuthService.updateRegistrationFlow(flowId, payload);

          return updateFlow$.pipe(
            switchMap(() => this.oryAuthService.getSession()),
            switchMap((session) => {
              if (session) {
                return of(OryAuthActions.submitOtpSuccess({ session }));
              }
              // Refresh flow to get updated state
              const getFlow$ =
                flowType === OryFlowType.LOGIN
                  ? this.oryAuthService.getLoginFlow(flowId)
                  : this.oryAuthService.getRegistrationFlow(flowId);

              return getFlow$.pipe(
                switchMap(() => this.oryAuthService.getSession()),
                filter((session): session is Session => !!session),
                map((session) => OryAuthActions.submitOtpSuccess({ session })),
              );
            }),
            catchError((error) => {
              if (error?.response?.status === 400) {
                // Check if the response contains error messages (invalid OTP, etc.)
                const hasErrorMessages =
                  error?.response?.data?.ui?.messages?.some(
                    (msg: OryMessage) => msg.type === 'error',
                  );

                if (hasErrorMessages) {
                  // This is a validation error (like invalid OTP), treat as failure
                  return of(
                    OryAuthActions.submitOtpFailure({
                      error: this.mapError(error),
                    }),
                  );
                }

                // No error messages, try to handle as potential success
                try {
                  return this.oryAuthService.getSession().pipe(
                    switchMap((session) => {
                      if (session) {
                        return of(OryAuthActions.submitOtpSuccess({ session }));
                      }

                      const getFlow$ =
                        flowType === OryFlowType.LOGIN
                          ? this.oryAuthService.getLoginFlow(flowId)
                          : this.oryAuthService.getRegistrationFlow(flowId);

                      return getFlow$.pipe(
                        map((flow) =>
                          OryAuthActions.submitOtpSuccess({ flow }),
                        ),
                      );
                    }),
                    catchError(() =>
                      of(
                        OryAuthActions.submitOtpFailure({
                          error: this.mapError(error),
                        }),
                      ),
                    ),
                  );
                } catch {
                  return of(
                    OryAuthActions.submitOtpFailure({
                      error: this.mapError(error),
                    }),
                  );
                }
              }

              if (error?.response?.status === 410) {
                return of(OryAuthActions.handleFlowExpired({ flowType }));
              }

              return of(
                OryAuthActions.submitOtpFailure({
                  error: this.mapError(error),
                }),
              );
            }),
          );
        },
      ),
    ),
  );

  // OTP Resend Effects
  resendOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OryAuthActions.resendOtp),
      switchMap(
        ({ flowId, flowType, csrfToken, loginData, registrationData }) => {
          let payload: any = {
            method: 'code',
            resend: 'code',
            csrf_token: csrfToken,
          };

          // For registration flow, include the original registration data
          if (flowType === OryFlowType.REGISTRATION && registrationData) {
            payload = {
              ...payload,
              'traits.email': registrationData.email,
              'traits.name.first': registrationData.firstName,
              'traits.name.last': registrationData.lastName,
            };
          } else if (flowType === OryFlowType.LOGIN && loginData) {
            payload = {
              ...payload,
              identifier: loginData.email,
            };
          }

          const updateFlow$ =
            flowType === OryFlowType.LOGIN
              ? this.oryAuthService.updateLoginFlow(flowId, payload)
              : this.oryAuthService.updateRegistrationFlow(flowId, payload);

          return updateFlow$.pipe(
            switchMap(() => {
              // Get the updated flow to ensure we have the latest state
              const getFlow$ =
                flowType === OryFlowType.LOGIN
                  ? this.oryAuthService.getLoginFlow(flowId)
                  : this.oryAuthService.getRegistrationFlow(flowId);

              return getFlow$.pipe(
                map((flow) => OryAuthActions.resendOtpSuccess({ flow })),
              );
            }),
            catchError((error) => {
              // Handle 400 status codes as success (OTP was likely sent)
              if (error?.response?.status === 400) {
                return of(OryAuthActions.resendOtpSuccess({}));
              }
              if (error?.response?.status === 410) {
                return of(OryAuthActions.handleFlowExpired({ flowType }));
              }
              return of(
                OryAuthActions.resendOtpFailure({
                  error: this.mapError(error),
                }),
              );
            }),
          );
        },
      ),
    ),
  );

  // Start countdown after successful resend
  startCountdownAfterSendOtp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        OryAuthActions.resendOtpSuccess,
        OryAuthActions.submitLoginSuccess,
        OryAuthActions.submitRegistrationSuccess,
      ),
      map(() => OryAuthActions.startResendCountdown()),
    ),
  );

  // Countdown timer effect
  resendCountdown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OryAuthActions.startResendCountdown),
      switchMap(() =>
        timer(0, 1000).pipe(
          // Emit every second
          take(61), // Take 61 values (0 to 60 seconds)
          map((tick) => 60 - tick), // Count down from 60
          takeUntil(
            this.actions$.pipe(
              ofType(
                OryAuthActions.clearFlowState,
                OryAuthActions.removeResendCountdown,
                OryAuthActions.navigateToLogin,
                OryAuthActions.navigateToRegistration,
                OryAuthActions.navigateToLanding,
                OryAuthActions.navigateToOtpVerify,
                OryAuthActions.navigateToAuthRedirect,
              ),
            ),
          ),
          map((countdown) =>
            OryAuthActions.updateResendCountdown({ countdown }),
          ),
        ),
      ),
    ),
  );

  // Logout Effects
  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OryAuthActions.logout),
      switchMap(() =>
        this.oryAuthService.logout().pipe(
          map(() => OryAuthActions.logoutSuccess()),
          catchError((error) =>
            of(
              OryAuthActions.logoutFailure({
                error: this.mapError(error),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  logoutSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OryAuthActions.logoutSuccess),
      switchMap(() => of(OryAuthActions.navigateToLanding())),
    ),
  );

  // Navigation Effects
  navigateToLogin$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OryAuthActions.navigateToLogin),
        tap(({ flowId }) => {
          const queryParams: Record<string, string> = {};
          if (flowId) queryParams['flow'] = flowId;

          this.router.navigate(['/', OryAuthPages.ROOT, OryAuthPages.LOGIN], {
            queryParams,
            replaceUrl: true,
            skipLocationChange: true,
          });
        }),
      ),
    { dispatch: false },
  );

  navigateToRegistration$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OryAuthActions.navigateToRegistration),
        tap(({ flowId }) => {
          const queryParams: Record<string, string> = {};
          if (flowId) queryParams['flow'] = flowId;

          this.router.navigate(
            ['/', OryAuthPages.ROOT, OryAuthPages.REGISTER],
            {
              queryParams,
              replaceUrl: true,
              skipLocationChange: true,
            },
          );
        }),
      ),
    { dispatch: false },
  );

  navigateToOtpVerify$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OryAuthActions.navigateToOtpVerify),
        tap(({ flowId, email }) => {
          const queryParams: Record<string, string> = { flow: flowId };
          if (email) queryParams['email'] = email;

          this.router.navigate(['/', OryAuthPages.ROOT, OryAuthPages.OTP], {
            queryParams,
          });
        }),
      ),
    { dispatch: false },
  );

  // Reset countdown when navigating to OTP verify
  resetCountdownOnOtpNavigation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OryAuthActions.navigateToOtpVerify),
      map(() => OryAuthActions.startResendCountdown()),
    ),
  );

  navigateToLanding$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OryAuthActions.navigateToLanding),
        tap(() => {
          this.router.navigateByUrl('/', { replaceUrl: true }).then(() => {
            window.location.reload();
          });
        }),
      ),
    { dispatch: false },
  );

  // Flow Expiration Handling
  handleLoginFlowExpired$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OryAuthActions.handleFlowExpired),
      switchMap(({ flowType }) => {
        if (flowType === OryFlowType.LOGIN) {
          return of(OryAuthActions.navigateToLogin({}));
        }
        return of(OryAuthActions.navigateToRegistration({}));
      }),
    ),
  );

  // Success Navigation
  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OryAuthActions.submitLoginSuccess),
      withLatestFrom(this.store.select(selectLoginFlow)),
      switchMap(([{ session, requiresOtp, flow }, loginFlow]) => {
        if (session) {
          return of(OryAuthActions.navigateToAuthRedirect({}));
        }

        if (requiresOtp && flow) {
          // Extract email from the flow UI nodes to pass to OTP verification
          const email =
            getNodeValue(loginFlow?.ui?.nodes, 'identifier') ||
            getNodeValue(flow?.ui?.nodes, 'identifier') ||
            undefined;

          return of(
            OryAuthActions.navigateToOtpVerify({
              flowId: flow.id,
              email,
            }),
          );
        }
        return of();
      }),
    ),
  );

  registrationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OryAuthActions.submitRegistrationSuccess),
      withLatestFrom(
        this.store.select(selectRegistrationFlow),
        this.store.select(selectRedirectTo),
      ),
      switchMap(
        ([{ session, requiresOtp, flow }, registrationFlow, redirectTo]) => {
          if (session) {
            return of(
              OryAuthActions.navigateToAuthRedirect({
                redirectTo,
              }),
            );
          }

          if (requiresOtp && flow) {
            // Extract email from the flow traits
            const email =
              getNodeValue(registrationFlow?.ui?.nodes, 'traits.email') ||
              getNodeValue(flow?.ui?.nodes, 'traits.email') ||
              undefined;

            return of(
              OryAuthActions.navigateToOtpVerify({
                flowId: flow.id,
                email,
              }),
            );
          }
          return of();
        },
      ),
    ),
  );

  otpSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OryAuthActions.submitOtpSuccess),
      withLatestFrom(this.store.select(selectRedirectTo)),
      // eslint-disable-next-line arrow-body-style
      switchMap(([, redirectTo]) => {
        return this.authFacade.initialize().pipe(
          // eslint-disable-next-line arrow-body-style
          concatMap(() => {
            return of(OryAuthActions.navigateToAuthRedirect({ redirectTo }));
          }),
        );
      }),
    ),
  );

  navigateToAuthRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(OryAuthActions.navigateToAuthRedirect),
        tap(({ redirectTo }) => {
          if (redirectTo) {
            this.router.navigateByUrl(redirectTo);
            return;
          }

          this.router.navigate(['/', Module.AuthRedirect]);
        }),
      ),
    { dispatch: false },
  );

  private mapError(error: any): AuthError {
    const response = error?.response;
    const uiMessage = response?.data?.ui?.messages?.[0] as
      | OryMessage
      | undefined;
    const isErrorUiMessage = uiMessage?.type === 'error';
    return {
      message: isErrorUiMessage
        ? uiMessage?.text
        : error?.message || 'An unexpected error occurred',
      code: uiMessage?.id || response?.data?.error?.id,
      status: response?.status,
    };
  }
}
